import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Mail, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';

/**
 * Outgoing mail, which exists for exactly one thing: the reset link on the
 * login page.
 *
 * Until this is filled in the login page tells people to ask an administrator
 * instead of offering a link that would go nowhere.
 */
export default function MailSection({ currentUserEmail }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['mailConfig'],
    queryFn: () => api.request('/mail-config'),
  });

  const [form, setForm] = useState({
    domain: '',
    from: '',
    region: 'us',
    apiKey: '',
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      domain: data.domain ?? '',
      from: data.from ?? '',
      region: data.region ?? 'us',
      // Never populated from the server — there is no route that returns it.
      apiKey: '',
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.request('/mail-config', {
        method: 'PUT',
        body: {
          domain: form.domain.trim(),
          from: form.from.trim(),
          region: form.region,
          // Omitted entirely when left blank, which the server reads as "keep
          // the one you have" — so the from-address can be corrected without
          // re-typing the key.
          ...(form.apiKey ? { apiKey: form.apiKey } : {}),
        },
      }),
    onSuccess: () => {
      setForm((prev) => ({ ...prev, apiKey: '' }));
      queryClient.invalidateQueries({ queryKey: ['mailConfig'] });
      toast.success('Mail settings saved and checked with Mailgun');
    },
    onError: (error) => toast.error(error.message ?? 'Could not save the mail settings'),
  });

  const testMutation = useMutation({
    mutationFn: ({ to, kind }) =>
      api.request('/mail-config/test', { method: 'POST', body: { to, kind } }),
    onSuccess: (result) =>
      toast.success(
        result.kind === 'reset'
          ? `Password reset sample sent to ${result.sentTo}`
          : `Tracking email sample sent to ${result.sentTo}`,
      ),
    onError: (error) => toast.error(error.message ?? 'Could not send the test message'),
  });

  const configured = Boolean(data?.domain && data?.from && data?.apiKeySet);

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-cyan-50">
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Outgoing email
          {configured && (
            <Badge className="bg-green-100 text-green-800 border-green-300 gap-1 hover:bg-green-100">
              <CheckCircle2 className="w-3 h-3" />
              configured
            </Badge>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        <Alert className="border-slate-200 bg-slate-50">
          <AlertDescription className="text-slate-700">
            Sent through Mailgun over HTTPS, so it works from anywhere the app can reach the
            internet — there are no mail ports to open. Used only for password reset links.
            Until it is set up, the sign-in page tells people to ask an administrator rather
            than offering a link that would go nowhere. Settings are checked with Mailgun
            before they save.
          </AlertDescription>
        </Alert>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              saveMutation.mutate();
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="mg-domain">Sending domain</Label>
              <Input
                id="mg-domain"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                placeholder="mg.goldstarprinting.us"
                required
              />
              <p className="text-xs text-slate-500">
                Exactly as it appears in the Mailgun dashboard — just the domain, not a web
                address. A sandbox domain works for testing, but only delivers to addresses
                added to it in Mailgun first.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mg-key">
                API key {data?.apiKeySet && <span className="text-slate-500">(one is set)</span>}
              </Label>
              <Input
                id="mg-key"
                type="password"
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
                value={form.apiKey}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                placeholder={data?.apiKeySet ? 'Leave blank to keep the current one' : 'key-…'}
              />
              <p className="text-xs text-slate-500">
                From the Mailgun dashboard, under sending keys. Never shown again once saved.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mg-from">Send from</Label>
              <Input
                id="mg-from"
                type="email"
                value={form.from}
                onChange={(e) => setForm({ ...form, from: e.target.value })}
                placeholder="shipping@mg.goldstarprinting.us"
                required
              />
              <p className="text-xs text-slate-500">
                The address staff will see the reset email come from. It has to be on the
                sending domain above.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mg-region">Region</Label>
              <select
                id="mg-region"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="us">United States</option>
                <option value="eu">Europe</option>
              </select>
              <p className="text-xs text-slate-500">
                Whichever the Mailgun account was created in. A key from one region is refused
                by the other, which reads as an invalid key rather than a wrong region.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                disabled={saveMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save mail settings
                  </>
                )}
              </Button>

              {/*
                Two buttons rather than one, because they answer different
                questions. "Does mail work at all" is answered by either; "what
                does the customer actually receive" is only answered by the
                tracking sample, and that is the one worth looking at before it
                goes out on a real shipment.
              */}
              {configured && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={testMutation.isPending}
                    onClick={() => {
                      const to = window.prompt(
                        'Send a sample tracking email to which address?',
                        currentUserEmail ?? '',
                      );
                      if (to) testMutation.mutate({ to: to.trim(), kind: 'tracking' });
                    }}
                    className="gap-2"
                  >
                    {testMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Send sample tracking email
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    disabled={testMutation.isPending}
                    onClick={() => {
                      const to = window.prompt(
                        'Send a sample password reset to which address?',
                        currentUserEmail ?? '',
                      );
                      if (to) testMutation.mutate({ to: to.trim(), kind: 'reset' });
                    }}
                    className="gap-2"
                  >
                    Password reset sample
                  </Button>
                </>
              )}
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
